// Slide-out location picker rendered beneath the LocationBadge pill.
//
// Autocomplete is powered by `downshift`'s useCombobox: full keyboard
// navigation, ARIA combobox semantics, and selection handling. The suggestion
// list blends the visitor's browser-cached "recent" places (when no query) with
// live matches against the in-browser OpenDB reference (no network). A footer
// re-detects the device position via the Geolocation API.
//
// The panel is always mounted; visibility is driven by `panelOpen` from the
// location context so it can animate (slide + fade) in and out.

import { useEffect, useRef, useState } from 'react';
import { useCombobox } from 'downshift';
import { IonIcon } from '@ionic/react';
import { searchOutline, locateOutline, closeOutline, timeOutline } from 'ionicons/icons';
import { useLocationContext } from '@/lib/geo/LocationContext';
import { searchPlaces, PLACE_LABEL, type NamedPlace } from '@/lib/geo/places';

const PANEL_BG = 'rgba(17, 24, 39, 0.97)';

const rowBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  border: 'none',
  color: '#e5e7eb',
  padding: '10px 14px',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: '0.85rem',
};

export function LocationPanel() {
  const { panelOpen, setPanelOpen, recent, selectPlace, redetect, status } = useLocationContext();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<NamedPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasQuery = query.trim().length > 0;
  const items = hasQuery ? hits : recent;

  const {
    isOpen,
    highlightedIndex,
    getMenuProps,
    getInputProps,
    getItemProps,
    setInputValue,
  } = useCombobox<NamedPlace>({
    items,
    itemToString: (item) => (item ? item.name : ''),
    onInputValueChange: ({ inputValue }) => setQuery(inputValue ?? ''),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) void selectPlace(selectedItem);
    },
  });

  // Live autosearch against the in-browser dataset (debounced).
  useEffect(() => {
    if (!hasQuery) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      searchPlaces(query, 12)
        .then((res) => {
          setHits(res);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }, 120);
    return () => clearTimeout(t);
  }, [query, hasQuery]);

  // Reset + focus whenever the panel opens/closes.
  useEffect(() => {
    if (panelOpen) {
      inputRef.current?.focus();
    } else {
      setQuery('');
      setInputValue('');
      setHits([]);
    }
  }, [panelOpen, setInputValue]);

  return (
    <>
      {panelOpen && (
        <div
          onClick={() => setPanelOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          aria-hidden
        />
      )}
      <div
        role="dialog"
        aria-label="Choose your location"
        aria-hidden={!panelOpen}
        style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          zIndex: 50,
          background: PANEL_BG,
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '16px',
          boxShadow: '0 18px 44px rgba(0,0,0,0.45)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          overflow: 'hidden',
          maxHeight: panelOpen ? '360px' : '0px',
          opacity: panelOpen ? 1 : 0,
          transform: panelOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.98)',
          transformOrigin: 'top center',
          pointerEvents: panelOpen ? 'auto' : 'none',
          transition:
            'opacity 0.22s cubic-bezier(0.4,0,0.2,1), transform 0.22s cubic-bezier(0.4,0,0.2,1), max-height 0.24s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <IonIcon icon={searchOutline} style={{ fontSize: '1rem', color: '#9ca3af', flexShrink: 0 }} />
          <input
            {...getInputProps({ ref: inputRef })}
            placeholder="Search a suburb, city or town…"
            aria-label="Search for a location"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: '0.9rem',
            }}
          />
          {hasQuery && (
            <button
              type="button"
              onClick={() => setInputValue('')}
              aria-label="Clear search"
              style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex' }}
            >
              <IonIcon icon={closeOutline} style={{ fontSize: '1rem' }} />
            </button>
          )}
        </div>

        {/* Suggestions (recent when empty, live matches when typing) */}
        <ul
          {...getMenuProps()}
          style={{ listStyle: 'none', margin: 0, padding: 0, overflowY: 'auto', flex: 1 }}
        >
          {isOpen && !hasQuery && recent.length > 0 && (
            <li
              style={{
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#9ca3af',
                padding: '10px 14px 4px',
              }}
            >
              Recent
            </li>
          )}

          {isOpen &&
            items.map((item, index) => {
              const isHighlighted = highlightedIndex === index;
              const isRecent = !hasQuery;
              return (
                <li
                  key={`${item.name}:${item.latitude}:${item.longitude}`}
                  {...getItemProps({ item, index })}
                  style={{
                    ...rowBase,
                    background: isHighlighted ? 'rgba(59,130,246,0.18)' : 'transparent',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isRecent && <IonIcon icon={timeOutline} style={{ fontSize: '0.95rem', color: '#9ecbff' }} />}
                    {item.name}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                    {PLACE_LABEL[item.placeType] ?? item.placeType}
                  </span>
                </li>
              );
            })}

          {isOpen && loading && (
            <li style={{ padding: '14px', fontSize: '0.8rem', color: '#9ca3af' }}>Searching…</li>
          )}

          {isOpen && !loading && hasQuery && hits.length === 0 && (
            <li style={{ padding: '14px', fontSize: '0.8rem', color: '#9ca3af' }}>
              No matching places. Try a larger area.
            </li>
          )}

          {isOpen && !hasQuery && recent.length === 0 && (
            <li style={{ padding: '14px', fontSize: '0.8rem', color: '#9ca3af' }}>
              Start typing to find your area.
            </li>
          )}
        </ul>

        {/* Re-detect footer */}
        <button
          type="button"
          onClick={redetect}
          disabled={status === 'loading'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            border: 'none',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(59,130,246,0.12)',
            color: '#9ecbff',
            padding: '12px',
            cursor: status === 'loading' ? 'default' : 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          <IonIcon icon={locateOutline} style={{ fontSize: '1rem' }} />
          {status === 'loading' ? 'Detecting…' : 'Re-detect my location'}
        </button>
      </div>
    </>
  );
}
